/**
 * sfx.ts - Comic-style synthesized sound effects
 *
 * All sounds generated via Web Audio API oscillators.
 * No external audio files needed.
 * Cute/casual tone for ALL AGES rating.
 */

let ctx: AudioContext | null = null;
let sfxGain: GainNode | null = null;
let sfxEnabled = true;

function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  const AC = window.AudioContext || (window as any).webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  sfxGain = ctx.createGain();
  sfxGain.gain.value = 0.3;
  sfxGain.connect(ctx.destination);
  return ctx;
}

function ensureResumed(): void {
  if (ctx && ctx.state === 'suspended') {
    ctx.resume();
  }
}

/** Cute "boing" for scare */
export function playScare(): void {
  if (!sfxEnabled) return;
  const c = getCtx();
  if (!c || !sfxGain) return;
  ensureResumed();

  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(300, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(600, c.currentTime + 0.08);
  osc.frequency.exponentialRampToValueAtTime(200, c.currentTime + 0.2);
  gain.gain.setValueAtTime(0.4, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, c.currentTime + 0.25);
  osc.connect(gain).connect(sfxGain);
  osc.start(c.currentTime);
  osc.stop(c.currentTime + 0.25);
}

/** Sparkly "ding" for skill activation */
export function playSkillActivate(): void {
  if (!sfxEnabled) return;
  const c = getCtx();
  if (!c || !sfxGain) return;
  ensureResumed();

  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(800, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(1200, c.currentTime + 0.1);
  osc.frequency.exponentialRampToValueAtTime(1600, c.currentTime + 0.15);
  gain.gain.setValueAtTime(0.3, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, c.currentTime + 0.2);
  osc.connect(gain).connect(sfxGain);
  osc.start(c.currentTime);
  osc.stop(c.currentTime + 0.2);
}

/** "Ka-ching" coin sound for earning SP */
export function playCoin(): void {
  if (!sfxEnabled) return;
  const c = getCtx();
  if (!c || !sfxGain) return;
  ensureResumed();

  const osc1 = c.createOscillator();
  const osc2 = c.createOscillator();
  const gain = c.createGain();
  osc1.type = 'square';
  osc2.type = 'square';
  osc1.frequency.setValueAtTime(1500, c.currentTime);
  osc2.frequency.setValueAtTime(2000, c.currentTime + 0.06);
  gain.gain.setValueAtTime(0.15, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, c.currentTime + 0.15);
  osc1.connect(gain).connect(sfxGain);
  osc2.connect(gain);
  osc1.start(c.currentTime);
  osc1.stop(c.currentTime + 0.06);
  osc2.start(c.currentTime + 0.06);
  osc2.stop(c.currentTime + 0.15);
}

/** Soft "pop" for UI buttons */
export function playPop(): void {
  if (!sfxEnabled) return;
  const c = getCtx();
  if (!c || !sfxGain) return;
  ensureResumed();

  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(400, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(200, c.currentTime + 0.08);
  gain.gain.setValueAtTime(0.25, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, c.currentTime + 0.1);
  osc.connect(gain).connect(sfxGain);
  osc.start(c.currentTime);
  osc.stop(c.currentTime + 0.1);
}

/** Happy chime for day summary / level up */
export function playChime(): void {
  if (!sfxEnabled) return;
  const c = getCtx();
  if (!c || !sfxGain) return;
  ensureResumed();

  const notes = [523, 659, 784, 1047]; // C5 E5 G5 C6
  notes.forEach((freq, i) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const startTime = c.currentTime + i * 0.1;
    gain.gain.setValueAtTime(0.2, startTime);
    gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);
    osc.connect(gain).connect(sfxGain!);
    osc.start(startTime);
    osc.stop(startTime + 0.2);
  });
}

/** Whoosh for visitor entering */
export function playWhoosh(): void {
  if (!sfxEnabled) return;
  const c = getCtx();
  if (!c || !sfxGain) return;
  ensureResumed();

  const bufferSize = c.sampleRate * 0.15;
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }
  const source = c.createBufferSource();
  source.buffer = buffer;

  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(2000, c.currentTime);
  filter.frequency.exponentialRampToValueAtTime(500, c.currentTime + 0.15);
  filter.Q.value = 2;

  const gain = c.createGain();
  gain.gain.setValueAtTime(0.15, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, c.currentTime + 0.15);

  source.connect(filter).connect(gain).connect(sfxGain);
  source.start(c.currentTime);
}

/** Chain bonus sound — ascending tones */
export function playChainBonus(chainCount: number): void {
  if (!sfxEnabled) return;
  const c = getCtx();
  if (!c || !sfxGain) return;
  ensureResumed();

  const baseFreq = 400 + Math.min(chainCount, 5) * 100;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(baseFreq, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(baseFreq * 1.5, c.currentTime + 0.12);
  gain.gain.setValueAtTime(0.25, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, c.currentTime + 0.18);
  osc.connect(gain).connect(sfxGain);
  osc.start(c.currentTime);
  osc.stop(c.currentTime + 0.18);
}

/** Toggle SFX on/off */
export function toggleSfx(): boolean {
  sfxEnabled = !sfxEnabled;
  return sfxEnabled;
}

/** Check if SFX is enabled */
export function isSfxOn(): boolean {
  return sfxEnabled;
}

/** Set SFX volume */
export function setSfxVolume(vol: number): void {
  if (sfxGain) {
    sfxGain.gain.value = Math.max(0, Math.min(1, vol));
  }
}
